import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import IconButton from "@material-ui/core/IconButton";
import MoreVert from "@material-ui/icons/MoreVert";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import ChatIcon from "@material-ui/icons/Chat";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import SearchIcon from "@material-ui/icons/Search";
import SpellcheckIcon from "@material-ui/icons/Spellcheck";
import TranslateIcon from "@material-ui/icons/Translate";
import SubjectIcon from "@material-ui/icons/Subject";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";
import ShortTextIcon from "@material-ui/icons/ShortText";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import {
  Button,
  FormControlLabel,
  Hidden,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
} from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

let audioRecorder = null;
let audioChunks = [];
let audioStream = null;

const getSupportedAudioMimeType = () => {
  if (!window.MediaRecorder) return "";

  const mimeTypes = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];

  return mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || "";
};

const stopAudioTracks = () => {
  if (audioStream) {
    audioStream.getTracks().forEach(track => track.stop());
    audioStream = null;
  }
};

const resetAudioRecorder = () => {
  audioRecorder = null;
  audioChunks = [];
  stopAudioTracks();
};

const isWebChatFocused = () => {
  const webChatRoot = document.getElementById("kodelr-webchat-root");
  return Boolean(webChatRoot && webChatRoot.contains(document.activeElement));
};

const focusMessageInput = input => {
  if (!input || isWebChatFocused()) return;
  input.focus();
};

const stopRecording = () =>
  new Promise((resolve, reject) => {
    if (!audioRecorder || audioRecorder.state === "inactive") {
      reject(new Error("Recorder not active"));
      return;
    }

    audioRecorder.onstop = () => {
      const mimeType = audioRecorder.mimeType || getSupportedAudioMimeType() || "audio/webm";
      const blob = new Blob(audioChunks, { type: mimeType });
      resetAudioRecorder();
      resolve(blob);
    };

    audioRecorder.onerror = event => {
      resetAudioRecorder();
      reject(event.error || new Error("Recorder error"));
    };

    audioRecorder.stop();
  });

const cancelRecording = () =>
  new Promise(resolve => {
    if (!audioRecorder || audioRecorder.state === "inactive") {
      resetAudioRecorder();
      resolve();
      return;
    }

    audioRecorder.onstop = () => {
      resetAudioRecorder();
      resolve();
    };

    audioRecorder.stop();
  });

const getAudioExtension = mimeType => {
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("mpeg")) return "mp3";
  return "webm";
};

const MediaPreviewThumb = ({ file, className }) => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!file || !file.type?.startsWith("image/")) {
      setUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (url) {
    return <img src={url} alt="" className={className} />;
  }

  return <div className={className}>{file?.name?.split(".").pop()?.toUpperCase() || "FILE"}</div>;
};

const useStyles = makeStyles(theme => ({
  mainWrapper: {
    background: "#eee",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: 0,
      width: "100%",
    },
  },

  newMessageBox: {
    background: "#eee",
    width: "100%",
    display: "flex",
    padding: "7px",
    alignItems: "center",
  },
  aiToolsBar: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px 0",
    overflowX: "auto",
    backgroundColor: "#eee",
    ...theme.scrollbarStyles,
  },
  aiToolButton: {
    flex: "0 0 auto",
    textTransform: "none",
    borderRadius: 10,
    backgroundColor: "#fff",
  },
  aiToolsBarLoading: {
    opacity: 0.78,
  },
  aiIconButton: {
    color: "#10945f",
  },
  internalNoteIconButton: {
    color: "#9a7a00",
  },
  internalNoteIconButtonActive: {
    color: "#f2b600",
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: "#fff",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    position: "relative",
  },
  messageInputWrapperAiLoading: {
    paddingTop: 34,
    border: "1px solid rgba(0, 168, 132, 0.35)",
    boxShadow: "0 0 0 3px rgba(0, 168, 132, 0.08)",
  },
  internalNoteInputWrapper: {
    background: "#fff8d8",
    border: "1px solid #f2d46b",
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },
  aiInputStatus: {
    position: "absolute",
    top: 5,
    left: 12,
    right: 12,
    minHeight: 22,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#075e54",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: "18px",
    pointerEvents: "none",
  },
  aiInputStatusSpinner: {
    color: "#00a884",
    flex: "0 0 auto",
  },
  aiInputStatusText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  sendMessageIcons: {
    color: "grey",
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#eee",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },
  mediaPreviewPanel: {
    position: "relative",
    width: "100%",
    padding: "14px 54px 14px 18px",
    backgroundColor: "#f7f7f7",
  },
  mediaPreviewCancel: {
    position: "absolute",
    top: 8,
    left: 8,
    zIndex: 2,
  },
  mediaPreviewGrid: {
    display: "flex",
    alignItems: "stretch",
    gap: 12,
    overflowX: "auto",
    paddingLeft: 30,
    ...theme.scrollbarStyles,
  },
  mediaPreviewCard: {
    position: "relative",
    flex: "0 0 150px",
    width: 150,
  },
  mediaPreviewImage: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 150,
    height: 150,
    borderRadius: 8,
    objectFit: "cover",
    backgroundColor: "rgba(0, 0, 0, 0.15)",
    color: "#455a64",
    fontWeight: 700,
    fontSize: 13,
  },
  mediaPreviewName: {
    margin: "6px 0 0",
    maxWidth: 150,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#4f5b62",
    fontSize: 13,
  },
  mediaPreviewDelete: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 2,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "&:hover": {
      backgroundColor: "#fff",
    },
  },
  mediaPreviewAdd: {
    flex: "0 0 180px",
    minHeight: 178,
    border: "1px dashed rgba(0, 0, 0, 0.28)",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    textAlign: "center",
    padding: 12,
  },
  mediaPreviewInputBar: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    backgroundColor: "#eee",
  },

  mediaCaptionWrapper: {
    padding: 6,
    background: "#fff",
    display: "flex",
    borderRadius: 8,
    flex: 1,
    minWidth: 0,
  },

  mediaCaptionInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },

  selectedMediaName: {
    flex: "0 1 180px",
    minWidth: 80,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "gray",
    fontSize: 13,
  },

  emojiBox: {
    position: "absolute",
    bottom: 63,
    width: 40,
    borderTop: "1px solid #e8e8e8",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: 0,
    position: "absolute",
    bottom: "50px",
    background: "#ffffff",
    padding: "10px",
    border: "1px solid #CCC",
    left: 0,
    width: "100%",
    maxHeight: 360,
    overflowY: "auto",
    "& li": {
      listStyle: "none",
      "& a": {
        display: "block",
        padding: "8px",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxHeight: "32px",
        "&:hover": {
          background: "#F1F1F1",
          cursor: "pointer",
        },
      },
    },
  },
  quickAnswerCategory: {
    marginBottom: theme.spacing(1.5),
  },
  quickAnswerCategoryTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  quickAnswerTableHeader: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: theme.spacing(1),
    padding: "4px 0",
    fontSize: 12,
    fontWeight: 700,
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
  },
  quickAnswerItem: {
    display: "grid",
    gridTemplateColumns: "140px 1fr",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: "7px 0",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    cursor: "pointer",
    "&:hover": {
      background: "#F7F7F7",
    },
  },
  quickAnswerShortcut: {
    display: "inline-flex",
    width: "fit-content",
    padding: "2px 6px",
    borderRadius: 6,
    border: "1px solid rgba(0, 0, 0, 0.15)",
    color: "#b80000",
    fontWeight: 700,
    fontFamily: '"Inter", sans-serif',
  },
  quickAnswerContent: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const MessageInput = ({ ticketStatus, connectionId, showAiTools = true, onOpenSequences }) => {
  const classes = useStyles();
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiActiveAction, setAiActiveAction] = useState("");
  const [internalNoteMode, setInternalNoteMode] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [translateAnchorEl, setTranslateAnchorEl] = useState(null);
  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  const aiActions = [
    { action: "suggest", label: "Sugerir", icon: <ChatIcon fontSize="small" /> },
    { action: "search", label: "Buscar", icon: <SearchIcon fontSize="small" /> },
    { action: "spelling", label: "Ortografía", icon: <SpellcheckIcon fontSize="small" /> },
    { action: "summarize", label: "Resumir", icon: <SubjectIcon fontSize="small" /> },
    { action: "enlarge", label: "Ampliar", icon: <UnfoldMoreIcon fontSize="small" /> },
    { action: "friendly", label: "Amigable", icon: <FavoriteBorderIcon fontSize="small" /> },
    { action: "formal", label: "Formal", icon: <BusinessCenterIcon fontSize="small" /> },
    { action: "direct", label: "Directo", icon: <ShortTextIcon fontSize="small" /> },
    { action: "clear", label: "Claro", icon: <FormatAlignLeftIcon fontSize="small" /> },
  ];

  const translateLanguages = [
    { language: "en", label: "Inglés" },
    { language: "pt", label: "Portugués" },
    { language: "fr", label: "Francés" },
    { language: "es", label: "Español" },
    { language: "he", label: "Hebreo" },
  ];

  const quickAnswersByCategory = quickAnswers.reduce((groups, quickAnswer) => {
    const category = quickAnswer.category || "General";
    return {
      ...groups,
      [category]: [...(groups[category] || []), quickAnswer],
    };
  }, {});

  const getAiActionLabel = action => {
    if (action === "translate") return "Traducir";
    return aiActions.find(item => item.action === action)?.label || "IA";
  };

  useEffect(() => {
    focusMessageInput(inputRef.current);
  }, [replyingMessage]);

  useEffect(() => {
    focusMessageInput(inputRef.current);
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = e => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const replaceInputText = text => {
    const input = inputRef.current;
    if (!input) {
      setInputMessage(text);
      return;
    }

    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    const hasSelection = selectionEnd > selectionStart;

    if (!hasSelection) {
      setInputMessage(text);
      setTimeout(() => focusMessageInput(inputRef.current), 0);
      return;
    }

    const nextMessage = `${inputMessage.slice(0, selectionStart)}${text}${inputMessage.slice(selectionEnd)}`;
    setInputMessage(nextMessage);
    setTimeout(() => {
      focusMessageInput(inputRef.current);
      inputRef.current?.setSelectionRange(selectionStart, selectionStart + text.length);
    }, 0);
  };

  const getSelectedText = () => {
    const input = inputRef.current;
    if (!input) return "";

    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;
    return selectionEnd > selectionStart
      ? inputMessage.slice(selectionStart, selectionEnd)
      : "";
  };

  const handleAiAssist = async (action, language) => {
    if (ticketStatus !== "open" || aiLoading) return;

    setTranslateAnchorEl(null);
    setAiActiveAction(action);
    setAiLoading(true);
    try {
      const selectedText = getSelectedText();
      const { data } = await api.post(`/tickets/${ticketId}/ai/assist`, {
        action,
        language,
        text: selectedText || inputMessage,
      });

      if (data?.text) {
        replaceInputText(data.text);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setAiLoading(false);
      setAiActiveAction("");
    }
  };

  const handleQuickAnswersClick = async quickAnswer => {
    if (aiLoading) return;

    if (quickAnswer.actionType === "sequence" || quickAnswer.blocks?.length) {
      setLoading(true);
      setTypeBar(false);
      setInputMessage("");

      try {
        await api.post(`/quickAnswers/${quickAnswer.id}/send/${ticketId}`);
      } catch (err) {
        toastError(err);
      }

      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(`/messages/${ticketId}/render`, {
        body: quickAnswer.message || "",
      });
      setInputMessage(data.body || "");
    } catch (err) {
      toastError(err);
      setInputMessage(quickAnswer.message);
    }
    setLoading(false);
    setTypeBar(false);
  };

  const handleAddEmoji = e => {
    let emoji = e.native;
    setInputMessage(prevState => prevState + emoji);
  };

  const handleChangeMedias = e => {
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias(prevState => [...prevState, ...selectedMedias]);
    e.target.value = "";
  };

  const handleInputPaste = e => {
    if (e.clipboardData.files[0]) {
      setMedias(prevState => [...prevState, e.clipboardData.files[0]]);
    }
  };

  const handleRemoveMedia = index => {
    setMedias(prevState => prevState.filter((media, mediaIndex) => mediaIndex !== index));
  };

  const handleUploadMedia = async e => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach(media => {
      formData.append("medias", media);
    });
    formData.append("body", inputMessage.trim());

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
    setInputMessage("");
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    if (internalNoteMode) {
      const message = {
        read: 1,
        fromMe: true,
        body: inputMessage.trim(),
        internalNote: true,
        quotedMsg: replyingMessage,
      };

      try {
        await api.post(`/messages/${ticketId}`, message);
      } catch (err) {
        toastError(err);
      }

      setInputMessage("");
      setShowEmoji(false);
      setLoading(false);
      setReplyingMessage(null);
      return;
    }

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    if (internalNoteMode) return;

    setLoading(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        throw new Error("Audio recording is not supported by this browser");
      }

      audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedAudioMimeType();

      audioChunks = [];
      audioRecorder = new MediaRecorder(
        audioStream,
        mimeType ? { mimeType } : undefined
      );

      audioRecorder.ondataavailable = event => {
        if (event.data?.size > 0) {
          audioChunks.push(event.data);
        }
      };

      audioRecorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      resetAudioRecorder();
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async value => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: {
            searchParam: inputMessage.substring(1),
            connectionId,
          },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    if (internalNoteMode) return;

    setLoading(true);
    try {
      const blob = await stopRecording();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `${new Date().getTime()}.${getAudioExtension(blob.type)}`;
      formData.append("medias", blob, filename);
      formData.append("body", "");
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      await cancelRecording();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = event => {
    setAnchorEl(null);
  };

  const renderReplyingMessage = message => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          <div className={classes.replyginMsgBody}>
            {!message.fromMe && (
              <span className={classes.messageContactName}>
                {privacyCensorSettings.conversations
                  ? "Contacto privado"
                  : message.contact?.name}
              </span>
            )}
            {privacyCensorSettings.conversations
              ? "Mensaje citado privado"
              : message.body}
          </div>
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={loading || aiLoading || ticketStatus !== "open"}
          onClick={() => setReplyingMessage(null)}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  const renderAiToolsBar = () => {
    if (ticketStatus !== "open") return null;
    if (!showAiTools) return null;

    return (
      <div
        className={clsx(classes.aiToolsBar, {
          [classes.aiToolsBarLoading]: aiLoading,
        })}
      >
        {aiActions.slice(0, 3).map(item => (
          <Tooltip title={item.label} key={item.action}>
            <span>
              <Button
                size="small"
                variant="outlined"
                className={classes.aiToolButton}
                startIcon={item.icon}
                disabled={loading || recording || aiLoading}
                onClick={() => handleAiAssist(item.action)}
              >
                {item.label}
              </Button>
            </span>
          </Tooltip>
        ))}
        <Tooltip title="Traducir">
          <span>
            <Button
              size="small"
              variant="outlined"
              className={classes.aiToolButton}
              startIcon={<TranslateIcon fontSize="small" />}
              disabled={loading || recording || aiLoading}
              onClick={event => setTranslateAnchorEl(event.currentTarget)}
            >
              Traducir
            </Button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={translateAnchorEl}
          open={Boolean(translateAnchorEl)}
          onClose={() => setTranslateAnchorEl(null)}
        >
          {translateLanguages.map(item => (
            <MenuItem
              key={item.language}
              disabled={aiLoading}
              onClick={() => handleAiAssist("translate", item.language)}
            >
              {item.label}
            </MenuItem>
          ))}
        </Menu>
        {aiActions.slice(3).map(item => (
          <Tooltip title={item.label} key={item.action}>
            <span>
              <Button
                size="small"
                variant="outlined"
                className={classes.aiToolButton}
                startIcon={item.icon}
                disabled={loading || recording || aiLoading}
                onClick={() => handleAiAssist(item.action)}
              >
                {item.label}
              </Button>
            </span>
          </Tooltip>
        ))}
      </div>
    );
  };

  if (medias.length > 0)
    return (
      <Paper elevation={0} square className={classes.viewMediaInputWrapper}>
        <div
          className={classes.mediaPreviewPanel}
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            setMedias(prevState => [...prevState, ...Array.from(e.dataTransfer.files || [])]);
          }}
        >
          <IconButton
            aria-label="cancel-upload"
            component="span"
            className={classes.mediaPreviewCancel}
            onClick={e => {
              setMedias([]);
              setInputMessage("");
            }}
            disabled={loading}
          >
            <CancelIcon className={classes.sendMessageIcons} />
          </IconButton>
          {loading && <CircularProgress className={classes.circleLoading} />}
          <div className={classes.mediaPreviewGrid}>
            {medias.map((media, index) => (
              <div className={classes.mediaPreviewCard} key={`${media.name}-${index}`}>
                <IconButton
                  size="small"
                  className={classes.mediaPreviewDelete}
                  onClick={() => handleRemoveMedia(index)}
                  disabled={loading}
                  id="deleteMediaIcon"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                <MediaPreviewThumb file={media} className={classes.mediaPreviewImage} />
                <p className={classes.mediaPreviewName} title={media.name}>
                  {media.name}
                </p>
              </div>
            ))}
            <label className={classes.mediaPreviewAdd} htmlFor="upload-button">
              <IconButton component="span" disabled={loading}>
                <AddIcon />
              </IconButton>
              <span>Arrastra y suelta para agregar archivos</span>
            </label>
          </div>
        </div>
        <div className={classes.mediaPreviewInputBar}>
          <input
            multiple
            type="file"
            id="upload-button"
            disabled={loading}
            className={classes.uploadInput}
            onChange={handleChangeMedias}
          />
          <IconButton
            aria-label="emojiPicker"
            component="span"
            disabled={loading}
            onClick={e => setShowEmoji(prevState => !prevState)}
          >
            <MoodIcon className={classes.sendMessageIcons} />
          </IconButton>
          {showEmoji ? (
            <div className={classes.emojiBox}>
              <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                <Picker
                  perLine={16}
                  showPreview={false}
                  showSkinTones={false}
                  onSelect={handleAddEmoji}
                />
              </ClickAwayListener>
            </div>
          ) : null}
          <label htmlFor="upload-button">
            <IconButton component="span" disabled={loading}>
              <AddIcon className={classes.sendMessageIcons} />
            </IconButton>
          </label>
          <div className={classes.mediaCaptionWrapper}>
            <InputBase
              className={classes.mediaCaptionInput}
              placeholder="Enviar mensaje"
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={loading}
              onKeyPress={e => {
                if (loading || e.shiftKey) return;
                if (e.key === "Enter") {
                  handleUploadMedia(e);
                }
              }}
            />
          </div>
          <IconButton
            aria-label="send-upload"
            component="span"
            onClick={handleUploadMedia}
            disabled={loading}
          >
            <SendIcon className={classes.sendMessageIcons} />
          </IconButton>
        </div>
      </Paper>
    );
  else {
    return (
      <Paper square elevation={0} className={classes.mainWrapper}>
        {replyingMessage && renderReplyingMessage(replyingMessage)}
        {renderAiToolsBar()}
        <div className={classes.newMessageBox}>
          <Hidden only={["sm", "xs"]}>
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={loading || aiLoading || recording || ticketStatus !== "open"}
              onClick={e => setShowEmoji(prevState => !prevState)}
            >
              <MoodIcon className={classes.sendMessageIcons} />
            </IconButton>
            {showEmoji ? (
              <div className={classes.emojiBox}>
                <ClickAwayListener onClickAway={e => setShowEmoji(false)}>
                  <Picker
                    perLine={16}
                    showPreview={false}
                    showSkinTones={false}
                    onSelect={handleAddEmoji}
                  />
                </ClickAwayListener>
              </div>
            ) : null}

            <input
              multiple
              type="file"
              id="upload-button"
              disabled={loading || aiLoading || recording || internalNoteMode || ticketStatus !== "open"}
              className={classes.uploadInput}
              onChange={handleChangeMedias}
            />
            <label htmlFor="upload-button">
              <IconButton
                aria-label="upload"
                component="span"
                disabled={loading || aiLoading || recording || internalNoteMode || ticketStatus !== "open"}
              >
                <AttachFileIcon className={classes.sendMessageIcons} />
              </IconButton>
            </label>
            <FormControlLabel
              style={{ marginRight: 7, color: "gray" }}
              label={i18n.t("messagesInput.signMessage")}
              labelPlacement="start"
              control={
                <Switch
                  size="small"
                  checked={signMessage}
                  disabled={aiLoading || internalNoteMode}
                  onChange={e => {
                    setSignMessage(e.target.checked);
                  }}
                  name="showAllTickets"
                  color="primary"
                />
              }
            />
            <Tooltip title={internalNoteMode ? "Nota interna activa" : "Agregar nota interna"}>
              <IconButton
                aria-label="ai-assistant"
                component="span"
                disabled={loading || aiLoading || recording || ticketStatus !== "open"}
                onClick={() => setInternalNoteMode(prevState => !prevState)}
              >
                <ChatIcon
                  className={clsx(classes.internalNoteIconButton, {
                    [classes.internalNoteIconButtonActive]: internalNoteMode,
                  })}
                />
              </IconButton>
            </Tooltip>
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <IconButton
              aria-controls="simple-menu"
              aria-haspopup="true"
              disabled={aiLoading}
              onClick={handleOpenMenuClick}
            >
              <MoreVert></MoreVert>
            </IconButton>
            <Menu
              id="simple-menu"
              keepMounted
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuItemClick}
            >
              <MenuItem onClick={handleMenuItemClick}>
                <IconButton
                  aria-label="emojiPicker"
                  component="span"
                  disabled={loading || aiLoading || recording || ticketStatus !== "open"}
                  onClick={e => setShowEmoji(prevState => !prevState)}
                >
                  <MoodIcon className={classes.sendMessageIcons} />
                </IconButton>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <input
                  multiple
                  type="file"
                  id="upload-button"
                  disabled={loading || aiLoading || recording || internalNoteMode || ticketStatus !== "open"}
                  className={classes.uploadInput}
                  onChange={handleChangeMedias}
                />
                <label htmlFor="upload-button">
                  <IconButton
                    aria-label="upload"
                    component="span"
                    disabled={loading || aiLoading || recording || internalNoteMode || ticketStatus !== "open"}
                  >
                    <AttachFileIcon className={classes.sendMessageIcons} />
                  </IconButton>
                </label>
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <FormControlLabel
                  style={{ marginRight: 7, color: "gray" }}
                  label={i18n.t("messagesInput.signMessage")}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={signMessage}
                      disabled={aiLoading || internalNoteMode}
                      onChange={e => {
                        setSignMessage(e.target.checked);
                      }}
                      name="showAllTickets"
                      color="primary"
                    />
                  }
                />
              </MenuItem>
              <MenuItem onClick={handleMenuItemClick}>
                <Tooltip title={internalNoteMode ? "Nota interna activa" : "Agregar nota interna"}>
                  <IconButton
                    aria-label="ai-assistant"
                    component="span"
                    disabled={loading || aiLoading || recording || ticketStatus !== "open"}
                    onClick={() => setInternalNoteMode(prevState => !prevState)}
                  >
                    <ChatIcon
                      className={clsx(classes.internalNoteIconButton, {
                        [classes.internalNoteIconButtonActive]: internalNoteMode,
                      })}
                    />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            </Menu>
          </Hidden>
          <div
            className={clsx(classes.messageInputWrapper, {
              [classes.internalNoteInputWrapper]: internalNoteMode,
              [classes.messageInputWrapperAiLoading]: aiLoading,
            })}
          >
            {aiLoading && (
              <div className={classes.aiInputStatus}>
                <CircularProgress
                  size={16}
                  thickness={5}
                  className={classes.aiInputStatusSpinner}
                />
                <span className={classes.aiInputStatusText}>
                  IA trabajando en {getAiActionLabel(aiActiveAction)}...
                </span>
              </div>
            )}
            <InputBase
              inputRef={input => {
                focusMessageInput(input);
                input && (inputRef.current = input);
              }}
              className={classes.messageInput}
              placeholder={
                internalNoteMode
                  ? "Escribe una nota interna"
                  : ticketStatus === "open"
                  ? i18n.t("messagesInput.placeholderOpen")
                  : i18n.t("messagesInput.placeholderClosed")
              }
              multiline
              maxRows={5}
              value={inputMessage}
              onChange={handleChangeInput}
              disabled={recording || loading || aiLoading || ticketStatus !== "open"}
              onPaste={e => {
                ticketStatus === "open" && !internalNoteMode && handleInputPaste(e);
              }}
              onKeyPress={e => {
                if (loading || aiLoading || e.shiftKey) return;
                else if (e.key === "Enter") {
                  handleSendMessage();
                }
              }}
            />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {Object.entries(quickAnswersByCategory).map(([category, answers]) => (
                  <li className={classes.quickAnswerCategory} key={category}>
                    <div className={classes.quickAnswerCategoryTitle}>
                      {category}
                    </div>
                    <div className={classes.quickAnswerTableHeader}>
                      <span>Atajo</span>
                      <span>Contenido</span>
                    </div>
                    {answers.map(value => (
                      <div
                        className={classes.quickAnswerItem}
                        key={value.id}
                        onClick={() => !aiLoading && handleQuickAnswersClick(value)}
                      >
                        <span className={classes.quickAnswerShortcut}>
                          /{value.shortcut}
                        </span>
                        <span className={classes.quickAnswerContent}>
                          {value.actionType === "sequence"
                            ? "Secuencia"
                            : value.blocks?.length
                            ? `${value.blocks.length} bloque(s)`
                            : value.message}
                        </span>
                      </div>
                    ))}
                  </li>
                ))}
              </ul>
            ) : (
              <div></div>
            )}
          </div>
          {inputMessage ? (
            <IconButton
              aria-label="sendMessage"
              component="span"
              onClick={handleSendMessage}
              disabled={loading || aiLoading}
            >
              <SendIcon className={classes.sendMessageIcons} />
            </IconButton>
          ) : recording ? (
            <div className={classes.recorderWrapper}>
              <IconButton
                aria-label="cancelRecording"
                component="span"
                fontSize="large"
                disabled={loading}
                onClick={handleCancelAudio}
              >
                <HighlightOffIcon className={classes.cancelAudioIcon} />
              </IconButton>
              {loading ? (
                <div>
                  <CircularProgress className={classes.audioLoading} />
                </div>
              ) : (
                <RecordingTimer />
              )}

              <IconButton
                aria-label="sendRecordedAudio"
                component="span"
                onClick={handleUploadAudio}
                disabled={loading}
              >
                <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
              </IconButton>
            </div>
          ) : (
            <>
              <Tooltip title="Secuencias">
                <IconButton
                  aria-label="openSequences"
                  component="span"
                  disabled={loading || aiLoading}
                  onClick={onOpenSequences}
                >
                  <FlashOnIcon className={classes.sendMessageIcons} />
                </IconButton>
              </Tooltip>
              <IconButton
                aria-label="showRecorder"
                component="span"
                disabled={loading || aiLoading || internalNoteMode || ticketStatus !== "open"}
                onClick={handleStartRecording}
              >
                <MicIcon className={classes.sendMessageIcons} />
              </IconButton>
            </>
          )}
        </div>
      </Paper>
    );
  }
};

export default MessageInput;

